/* eslint-disable @next/next/no-img-element */
/* eslint-disable indent */
/**
 * Step Five: collect credit card info via Stripe Elements components and sync to Starbase
 */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { mq, storageMode, googleAnalyticsEvent, useBooleanState, getArrivalWindow, getAppointmentEndTime } from '../../../helpers'
import _ from 'lodash'
import { flow, map, sum, omit } from 'lodash/fp'
import {
  useMe,
  useAllMarketServices,
  claimUserCredit,
  bookAppointment,
  calcApptPrice
} from '../../../services/GraphQlClientService'
import Color from 'color'
import { gsColors } from '../../../styles/Variables'
import Button from './components/Button'
import Input from './components/Input'
import RegularInput from '../../Input'
import Label from './components/Label'
import checkmarkSvg from '../../../../public/images/checkmark.svg'
import LoadingStep from './components/LoadingStep'
import {
  formatDateLong,
  formatTimeShort,
  formatCurrency,
  serviceAndAddOnServiceInfo,
  trackCheckoutStarted,
} from '../../../helpers'

import { Section, Header2, Text } from '../../../styles/Components'
import PaymentCard from '../../checkout/PaymentCard'
import CalendarDropdown from '../../CalendarDropdown'


const SummarySection = styled(Section)`
  background-color: ${gsColors.khaki};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 37px 0 58px;
  width: 100%;
  max-width: 624px;

  ${mq('max', '768px')} {
    max-width: none;
    margin: 0 0 30px;
  }

  
  .summary-content {
    width: 100%;
    max-width: 450px;
    
    ${mq('max', '768px')} {
      max-width: none;
      padding: 0 16px;
    }

    .date-time {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      margin-bottom: 30px;

      img {
        margin-right: 18px;
      }

      .summary-detail {
        display: flex;
        flex-direction: column;
        

        p {
          ${mq('max', '768px')} {
            font-size: 16px;
          }
        }
      }
    }

    .arrival-window-text {
      margin-top: 5px;
      font-family: Euclid;

      ${mq('max', '768px')} {
        max-width: 100%;
      }

      b {
        font-weight: 600 !important;
      }
    }

    .date-time-content {
      display: flex;
      flex-wrap: wrap;
    }
  }

  .summary-divider {
    width: 100%;
    border-bottom: 1px solid ${gsColors.textGrey};
    margin-bottom: 38px;
  }

  .address {
    margin-bottom: 30px;

    img {
      width: 15px;
      height: 19px;
    }
  }

  .info-icon-container {
    display: inline-block;    
  }

  .prices {
    line-height: 100%;
    font-family: Euclid;

    li {
      color: ${gsColors.meredithGrey};
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 12px 0;


      &.service,
      &.total {
        color: ${gsColors.black};

      }
      
      &.service {
        margin: 18px 0;
        font-size: 18px;
        
        ${mq('max', '768px')} {
          font-size: 16px;
        }

        .name {
          text-transform: capitalize;
        }
      }

      &.credit {
        color: ${gsColors.cobalt};
      }

      &.subtotal {
        margin-top: 20px;
      }

      &.total {
        font-size: 20px;
        font-weight: 600;
      }

      &.promo-code {
        display: block;
        margin: 30px 0;
        font-family: Euclid !important;
        font-size: 18px !important;
        
        ${mq('min', '768px')} {
          margin-top: 20px;
        }
        
        label {
          color: ${gsColors.black};
          font-family: Euclid !important;
          font-size: 18px;
        }

        input, button {
          font-size: 16px;
        }
      }
    }
  }

  .input-group-btn {
    position: relative;

    input {
      padding-right: 56px;

      ${mq('min', '768px')} {
        font-size: 14px;
      }
    }
  }

  .promo-code-message {
    font-size: 13px;
    margin-top: 10px;
    text-align: center;

    &.success {
      color: ${gsColors.persianGreen};
    }

    &.error {
      color: ${gsColors.sunsetOrange};
    }
  }
`

const LeftColumn = styled.div`
  max-width: 600px;

  section {
    &:first-of-type {
      margin: 0 16px 30px;
      display: flex;
      flex-direction: column;
      align-items: center;
      
      ${mq('max', '768px')} {
        margin: 0 0 30px;
        padding: 30px 16px 18px;

        h2 {
          width: 100%;
        }
      }
      
      div {
        width: 100%;
        max-width: 379px;

        ${mq('max', '768px')} {
          max-width: none;ß
        }
      }
    }
  }
`

const WeddingDetailsSection = styled(Section)`
  max-width: 600px;
  padding: 50px 60px;

  ${mq('max', '768px')} {
    margin: 0 0 30px;
    max-width: none;
    padding: 50px 16px;
  }

  div {
    &:first-of-type {
      margin-bottom: 30px;

      ${mq('max', '768px')} {
        max-width: none;
      }
    }

    &:nth-of-type(2) {
      max-width: 379px;

      ${mq('max', '768px')} {
        max-width: none;
      }
    }
  }



  .gs-checkbox-circle {

    input:checked + label {
      .box {
        background-color: ${gsColors.sunsetOrange};
        background-image: url(${checkmarkSvg});
        box-shadow: none;
      }
    }

    input[type='checkbox'] {
      display: none;
    }

    .box {
      cursor: pointer;
      background: ${gsColors.hintOfRed} no-repeat center;
      box-shadow: inset 0 0 2px 0 ${Color(gsColors.black).alpha(0.2).string()};
      border-radius: 50%;
      display: inline-block;
      position: relative;
      transition: all 250ms;
      width: 24px;
      height: 24px;
      margin-right: 10px;
    }
    
    label {
      cursor: pointer;
      margin-top: 20px;
      content-align: center;
      display: flex;
    }
  }

  .input-group-btn {
    margin-top: 10px;
  }

  .wedding-date-input-group {
    border: none;
    border-bottom: 1px solid black;
    display: flex;
    align-items: center;
    padding-left: 5px;
  }

  .react-datepicker__input-container input {
    border: none;
    font-family: Brown, sans-serif;
    font-size: 16px;
    line-height: 20px;
    padding: 10px 0;
    width: 100%;
    margin-left: 10px;
  }


  .gs-checkbox-circle {
    margin-top: 10px;
  }
`

const PromoButton = styled(Button)`
  background-color: transparent;
  color: ${gsColors.sunsetOrange};
  font-size: 13px;
  padding: 0;
  position: absolute;
  top: 0;
  right: 0;
  transition: color 250ms;
  width: auto;
  height: 40px;

  &[disabled] {
    background-color: transparent;
    color: ${gsColors.venus};
  }
`

const Five = ({
  state: {
    address,
    market: { timezone },
    selectedAppointment: appointment,
    selectedPaymentMethodId,
    paymentMethods,
    step,
    isDesktop,
    weddingDetails: {
      weddingDate,
      weddingLocation,
      weddingOtherServices = []
    } = {},
    serviceRequests,
  },
  dispatch,
  setIsStepValid,
  code
}) => {
  if (typeof weddingDate === 'string') {
    dispatch({ weddingDetails: { weddingDate: new Date(weddingDate) } })
  }

  const team = _.chain(appointment?.appointmentServices || [])
    .map('providerId')
    .uniq()
    .value()
    .length || 1

  // promo code state variables
  const [promoCode, setPromoCode] = useState('') // used for actual promo code input field
  const [appliedPromoCode, setAppliedPromoCode] = useState('') // used to send promo code to tracking
  const [appliedPromoCodeAmt, setAppliedPromoCodeAmt] = useState(0)
  const [promoCodeClass, setPromoCodeClass] = useState('')
  const [promoCodeMessage, setPromoCodeMessage] = useState('')
  const [weddingCalendarOpen, toggleWeddingCalendar] = useBooleanState()

  const storage = storageMode('localStorage')
  // controls whether to display the menu to select an existing credit card
  const [hasExistingPaymentMethods, setHasExistingPaymentMethods] = useState(
    paymentMethods && paymentMethods.length
  )

  // apply promo code
  const applyPromoCode = async (promoCode) => {
    try {
      // claim
      const userCredits = await claimUserCredit(me.id, promoCode)

      // an array of credits is returned so sum up amounts
      const amount = userCredits[0].isFraction
        ? userCredits[0].amount * 100
        : flow(map('amount'), sum)(userCredits)

      // display amount applied
      setPromoCodeMessage(
        `${userCredits[0].isFraction ? `${amount}%` : formatCurrency(amount)
        } in credit has been applied to your account.`
      )
      setPromoCodeClass('success')
      setAppliedPromoCode(promoCode) // used to track what promo code used with rakuten
      setAppliedPromoCodeAmt(amount) // used to track how much the promo was with rakuten
      setPromoCode('') // clear input

      // recalculate appointment price w/ new promo credits applied
      updateAppointmentPrice()
    } catch (error) {
      // display any error including invalid/expired code
      setPromoCodeMessage(error.message)
      setPromoCodeClass('error')
    }
  }

  // load market services for service info
  const allMarketServices = useAllMarketServices()

  const marketServicesForMarket = _(allMarketServices)
    .filter(ms => ms.marketId === appointment.address.market?.id)
    .value()

  // load user info for payment methods
  const me = useMe()

  // track checkout started to segment when step 5 mounts
  useEffect(() => {
    if (!marketServicesForMarket.length) {
      return
    }

    trackCheckoutStarted(appointment, marketServicesForMarket, serviceRequests)
  }, [marketServicesForMarket, appointment, serviceRequests])


  // applies a promo code that was saved from app reducer
  useEffect(() => {
    if (!code || !me) {
      return
    }

    applyPromoCode(code)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, me])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])


  if (!me) {
    return <LoadingStep id="loading" />
  }
  //Store me as user info in local storage so the user remains logged in after booking flow

  storage.setItem(
    'userInfo',
    JSON.stringify(omit(['addresses', 'paymentMethods'], me))
  )

  // get an updated price for this appointment from the server which will take into
  // account any newly added promo codes
  const updateAppointmentPrice = () => {
    const date = DateTime.fromSeconds(appointment.actualStartDateTime, { zone: 'utc' }).setZone(timezone).toISO()
    const addressOmit = _.omit(address, ['id', 'latitude', 'longitude', 'formatted', '__typename'])
    const services = _.map(appointment.appointmentServices, (service) =>
      _.omit(service, [
        'provider',
        'service',
        'isSquadSearch',
        'isMember',
        'isProviderLocked'
      ])
    )

    if (!addressOmit.pets?.length) {
      addressOmit.pets = 'None'
    }

    calcApptPrice(date, addressOmit, services).then(
      ({
        creditsApplied,
        totalPrice,
        taxAmount,
        tipAmount,
        handlingFee,
        finalPriceWithTaxAndTip,
        additionalFees
      }) =>
        dispatch({
          selectedAppointment: {
            taxAmount,
            tipAmount,
            handlingFee,
            finalPriceWithTaxAndTip,
            totalPrice,
            creditsApplied,
            additionalFees
          }
        })
    )
  }

  // availability search isn't returning the grand total so fetch it
  if (appointment.finalPriceWithTaxAndTip === null) {
    updateAppointmentPrice(me.id, appointment)
  }

  // init existing payment methods and select one
  if (!paymentMethods) {
    if (
      me.paymentMethods &&
      me.paymentMethods.length &&
      !hasExistingPaymentMethods
    ) {
      // if we've just initialized payment methods update the state of existing payments methods
      setHasExistingPaymentMethods(true)
    }

    return dispatch({
      paymentMethods: me.paymentMethods,
      selectedPaymentMethodId: me.paymentMethods.length
        ? me.paymentMethods[0].id
        : null
    })
  }

  // step is valid if we have a payment method
  setIsStepValid(!!selectedPaymentMethodId, 'Book Now', async () => {
    // treat user as a new user in tracking if they havent booked in over a year
    const daysSinceLastBooked = Math.abs(DateTime.fromISO(me.lastAppointmentBookedAt).diffNow('days').days)
    const isNewUser = !me.lastAppointmentBookedAt || daysSinceLastBooked >= 365
    if (isNewUser) {
      googleAnalyticsEvent('book', { label: 'New User' })
    }

    if (includesWeddingServices) {
      appointment.notes =
        (appointment.notes || '') +
        `
  Wedding Details:
    Date: ${weddingDate
          ? DateTime.fromJSDate(weddingDate).toLocaleString(DateTime.DATE_SHORT)
          : 'n/a'
        }. 
    Location: ${weddingLocation ? weddingLocation : 'n/a'}. 
    Other Services: ${weddingOtherServices.length ? weddingOtherServices.join(', ') : 'none'
        }. 
  `
    }

    if (selectedPaymentMethodId) {
      appointment = {
        ...appointment,
        paymentMethodId: selectedPaymentMethodId
      }
    }

    const bookedAppointment = await bookAppointment(
      appointment,
      omit(
        [
          '__typename',
          'formatted',
          'latitude',
          'longitude',
          ...(!address?.pets || !address?.pets.length ? ['pets'] : []),
        ],
        address,
      ),
    )    

    dispatch({
      bookedAppointment,
      isNewUser,
      appliedPromoCode,
      appliedPromoCodeAmt,
      marketServicesForMarket,
      serviceRequests,
      step: step + 1
    })
  })

  // fetch service names, prices for summary
  const appointmentServiceInfo = appointment.appointmentServices.map(
    (appointmentService) => {
      const serviceInfo = serviceAndAddOnServiceInfo(marketServicesForMarket, {
        baseServiceId: appointmentService.baseServiceId,
        addOnServiceIds: appointmentService.addOnServiceIds
      })
      return {
        ...serviceInfo,
        baseServicePrice: appointmentService.price - _.sumBy(serviceInfo.addOnServices, 'price'),
        appointmentService
      }
    }
  )

  // check if we should display the wedding info section
  const weddingServiceNameRegex = /wedding|bridal|flower|bride/i
  const includesWeddingServices = !!appointmentServiceInfo.find(({ name }) =>
    weddingServiceNameRegex.test(name)
  )

  const arrivalWindow = getArrivalWindow(appointment.actualStartDateTime, timezone)
  const endTime = getAppointmentEndTime(appointment, timezone)

  return (
    <>
      <LeftColumn>
        <PaymentCard
          selectedPaymentMethodFn={selectedPaymentMethodId => {
            dispatch({ selectedPaymentMethodId })
          }}
          createdPaymentMethodFn={method =>
            dispatch({
              paymentMethods,
              selectedPaymentMethodId: method.id
            })
          }
          isDesktop={isDesktop}
          selectedPaymentMethodId={selectedPaymentMethodId}
          paymentMethods={paymentMethods}
          account={false}
        />

        {includesWeddingServices && (
          <WeddingDetailsSection>
            <Header2
              fontSize={36} 
              lineHeight='138%' 
              spacing={0.36}
              align='center'
              margin='0 0 30px'
            >
              Add wedding details.
            </Header2>
            <Label>Wedding Date</Label>
            <CalendarDropdown
              maxWidth={379}
              selected={weddingDate}
              toggle={toggleWeddingCalendar}
              open={weddingCalendarOpen}
              onClick={weddingDate => {
                dispatch({ weddingDetails: { weddingDate }})
                toggleWeddingCalendar()
              }}
            />
            <RegularInput 
              value={weddingLocation}
              placeholder='Wedding Location'
              onChange={({ target: { value: weddingLocation } }) =>
                dispatch({ weddingDetails: { weddingLocation } })
              }
            />
            <Label style={{ marginTop: '30px' }}>Are you interested in other wedding services?</Label>
            <div className="input-group-btn checkbox-list">
              {[
                'Bridal Party Hair',
                'Bridal Party Makeup',
                'Engagement Photos'
              ].map(otherServiceName => (
                <>
                  <div className="gs-checkbox-circle">
                    <input
                      type="checkbox"
                      name={otherServiceName}
                      id={otherServiceName}
                      checked={weddingOtherServices.includes(otherServiceName)}
                      onChange={({ target: { checked } }) => {
                        const updatedOtherServiceNames = checked
                          ? weddingOtherServices.concat([otherServiceName])
                          : weddingOtherServices.filter(
                            name => name !== otherServiceName
                          )

                        return dispatch({
                          weddingDetails: {
                            weddingOtherServices: updatedOtherServiceNames
                          }
                        })
                      }}
                    />
                    <label htmlFor={otherServiceName}>
                      <span className="box"></span>
                      {otherServiceName}
                    </label>
                  </div>
                </>
              ))}
            </div>
          </WeddingDetailsSection>
        )}
      </LeftColumn>
      <SummarySection>
        <Header2 
          fontSize={36} 
          lineHeight='138%' 
          spacing={0.36}
          align='center'
          margin='0 0 30px'
        >
          Appointment Summary
        </Header2>
        <div className='summary-content'>
          <div className='date-time'>
            <img src='/images/booking-checkout-calendar.svg' alt='date' />
            <div className='summary-detail'>
              <div className='date-time-content'>
                <Text fontSize={18} weight={600} lineHeight='140%'>
                  {formatDateLong(appointment.actualStartDateTime, timezone)}
                </Text>
                <Text fontSize={18} weight={600}>
                  &nbsp;&bull;&nbsp;
                  {formatTimeShort(appointment.actualStartDateTime, timezone)}
                  &nbsp;-&nbsp;
                  {endTime}
                </Text>
              </div>

              <div className='arrival-window-text' style={{ display: 'flex', width: '100%', flex: 1 }}>
                <Text fontSize={16} weight={400} style={{ flex: 1, width: '100%' }}>
                  Your Beauty Pro{team > 1 ? 's' : ''} will arrive between
                  {' '}
                  <b>{arrivalWindow[0]}</b> 
                  {' '}
                  and
                  {' '}
                  <b>{arrivalWindow[1]}</b>
                </Text>
              </div>
            </div>
          </div>
          <div className='date-time'>
            <img src='/images/booking-checkout-time.svg' alt='date' />
            <div className='summary-detail'>
              { address?.name && (
                <Text fontSize={18} weight={600} lineHeight='140%'>
                  {address.name}
                </Text>
              )}
              <Text fontSize={18} weight={600} lineHeight='140%'>
                {address.street}
              </Text>
              {
                address.apartment && (
                  <Text 
                    fontSize={18} 
                    lineHeight="140%"
                    weight={600}
                  >
                    {address.apartment}
                  </Text>
                )
              }
              <Text fontSize={18} weight={600} lineHeight='140%'>
                {address.city}, {address.state} {address.zip}
              </Text>
            </div>
          </div>
          <div className='summary-divider' />
          <ul className="prices">
            {appointmentServiceInfo.map(({ name, addOnServices, baseServicePrice }, i) => (
              <>
                <li key={i} className="service">
                  <span className="name">{name && name.toLowerCase()}</span>
                  <span>
                    {formatCurrency(baseServicePrice)}
                  </span>
                </li>
                {addOnServices &&
                  addOnServices.map(({ name, price }, j) => (
                    <li key={i * 100 + j} className="service">
                      <span>{name}</span>
                      <span>{formatCurrency(price)}</span>
                    </li>
                  ))}
              </>
            ))}
            <li className="subtotal">
              <span>Subtotal</span>
              <span>{formatCurrency(appointment.totalPrice)}</span>
            </li>
            {appointment.creditsApplied > 0 && (
              <li className="credit">
                <span>Credit</span>
                <span>-{formatCurrency(appointment.creditsApplied)}</span>
              </li>
            )}
            <li>
              <span>
                Tax &amp; Fees
              </span>
              <span>{formatCurrency(appointment.taxAmount + appointment.handlingFee)}</span>
            </li>
            <li>
              <span>
                Gratuity
              </span>
              <span>{formatCurrency(appointment.tipAmount)}</span>
            </li>
            {
              appointment.additionalFees.map((fee, i) => (
                <li key={i}>
                  <span>
                    {fee.description}
                  </span>
                  <span>{formatCurrency(fee.amount)}</span>
                </li>
              ))
            }
            <div className='summary-divider' style={{ margin: '32px 0 12px' }}/>
            <li className="promo-code">
              <div className="input-group-btn">
                <Input
                  style={{ backgroundColor: 'transparent' }}
                  type="text"
                  placeholder="Enter a promo code"
                  value={promoCode}
                  onChange={({ target: { value } }) => setPromoCode(value)}
                />
                <PromoButton disabled={!promoCode} onClick={() => applyPromoCode(promoCode)}>
                  Apply
                </PromoButton>
              </div>
              {promoCodeMessage && (
                <p className={`promo-code-message ${promoCodeClass}`}>
                  {promoCodeMessage}
                </p>
              )}
            </li>
            <li className="total">
              <span>Total</span>
              <span>{formatCurrency(appointment.finalPriceWithTaxAndTip)}</span>
            </li>
          </ul> 
        </div>
      </SummarySection>
    </>
  )
}

Five.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired
}

export default Five
